import React from 'react';
import { Clock, Users } from 'lucide-react';
import { TelegramListItem, Avatar, Badge } from '@tadagram/ui';

/**
 * WorkerItem - Individual worker in the list
 * Design based on Telegram's ChatItem pattern
 * 
 * Features:
 * - Worker name/ID
 * - Expiration date/time
 * - Account count in this worker
 * - Online/offline indicator
 * - Active state highlighting
 */

export interface Worker {
  uuid: string;
  workerName: string;
  expiresAt?: string; // ISO date string
  accountCount: number;
  isOnline: boolean;
  status: 'active' | 'inactive' | 'expired';
}

interface WorkerItemProps {
  worker: Worker;
  active: boolean;
  onClick: () => void;
}

const formatExpiry = (expiresAt?: string): string => {
  if (!expiresAt) return 'No expiry';
  
  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMs < 0) return 'Expired';
  
  const day = String(expiry.getDate()).padStart(2, '0');
  const month = String(expiry.getMonth() + 1).padStart(2, '0');
  const year = String(expiry.getFullYear()).slice(-2);
  const dateStr = `${day}/${month}/${year}`;
  
  if (diffDays > 0) {
    return `${dateStr} (${diffDays}d left)`;
  }
  
  return `${dateStr} (Expiring today)`;
};

export const WorkerItem: React.FC<WorkerItemProps> = ({
  worker,
  active,
  onClick
}) => {
  const expiryText = formatExpiry(worker.expiresAt);
  const isExpired = worker.status === 'expired';
  const isInactive = worker.status === 'inactive';

  return (
    <TelegramListItem
      onClick={onClick}
      active={active}
      disabled={isExpired || isInactive}
      title={worker.workerName}
      subtitle={
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={12} />
          {expiryText}
        </span>
      }
      avatar={
        <Avatar variant="default">
          W
        </Avatar>
      }
      badge={worker.isOnline ? <Badge variant="success" style={{ position: 'absolute', bottom: -2, right: -2, width: 10, height: 10, borderRadius: '50%', padding: 0 }}>{''}</Badge> : undefined}
      meta={
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <Users size={14} />
          {worker.accountCount}
        </div>
      }
    />
  );
};

export default WorkerItem;
